import { useTranslator } from '@Localization';
import { IconButton } from '@mui/joy';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { TrashIcon } from '../../../../../../design/static/icons/TrashIcon';
import { ConfirmationModal } from '../../../../../components/ConfirmationModal';
import { EnableFor } from '../../../../../containers/EnableForRole';
import { ToolDeleteStrategy } from '../../../projectDetails.models';
import { useProjectDetailsPermissions } from '../../../projectDetails.permissions';
import { IProjectDetailsStore } from '../../../projectDetails.store';
import { mapToolType, ToolType } from '../helpers/tools.helpers';
import { IToolAffect, IToolUsage } from '../projectDetailsTools.models';
import { AnnotationDiscardConfirmModal } from './AnnotationDiscardConfirmModal';
import { ToolDeleteModal } from './ToolDeleteModal';

interface AnnotationTypeDeleteProps {
  isLoading: boolean;
  isUsedAsScope: boolean;
  showModal: boolean;
  showDiscardConfirmModal: boolean;
  toolUsage?: IToolUsage;
  strategy: ToolDeleteStrategy;
  toolAffect?: IToolAffect;
  onDelete: () => Promise<void>;
  onCancel: () => void;
  onDeleteConfirmed: () => Promise<void>;
  onStrategyChange: (strategy: ToolDeleteStrategy) => void;
  onCancelDiscard: () => void;
  onConfirmDiscard: () => Promise<void>;
}

export const AnnotationTypeDelete = observer((props: AnnotationTypeDeleteProps): ReactElement => {
  const { t } = useTranslator();
  const projectDetailsPermissions = useProjectDetailsPermissions();
  const projectDetailsStore = useInjection(IProjectDetailsStore.$);
  const canEdit = projectDetailsPermissions.canEditProject(projectDetailsStore.status);

  return (
    <>
      <EnableFor hasPermission={canEdit}>
        <IconButton
          size='sm'
          onClick={() => {
            void props.onDelete();
          }}
          loading={props.isLoading}
          disabled={props.isUsedAsScope || props.isLoading}
          title={props.isUsedAsScope ? t('tool_is_used_as_scope') : t('remove_tool')}
        >
          <TrashIcon />
        </IconButton>
      </EnableFor>
      {props.toolUsage && props.toolUsage.occurrenceCount > 0 && (
        <ToolDeleteModal
          strategy={props.strategy}
          showModal={props.showModal}
          onConfirm={props.onDeleteConfirmed}
          onCancel={props.onCancel}
          onStrategyChange={props.onStrategyChange}
          toolType={mapToolType(ToolType.MarkingTool)}
          annotationsCount={props.toolUsage.imageCount || 0}
          objectsCount={props.toolUsage.occurrenceCount || 0}
        />
      )}

      {props.toolUsage && props.toolUsage.occurrenceCount === 0 && (
        <ConfirmationModal
          confirmationHeader={'confirm_delete'}
          confirmationQuestion={'are_you_sure_you_want_to_delete_tool'}
          onCancel={props.onCancel}
          onConfirm={props.onDeleteConfirmed}
          showModal={props.showModal}
          confirmationYes={'delete'}
          loaderKey='tool-delete-confirm-button'
        />
      )}

      <AnnotationDiscardConfirmModal
        onCancel={props.onCancelDiscard}
        onConfirm={props.onConfirmDiscard}
        showModal={props.showDiscardConfirmModal}
        annotationsCount={props.toolUsage?.imageCount || 0}
        objectsCount={props.toolAffect?.objectsCount || 0}
        questionsCount={props.toolAffect?.questionsCount || 0}
        attributesCount={props.toolAffect?.attributesCount || 0}
      />
    </>
  );
});
